import { FC, useMemo } from 'react';
import { IconButton, Stack, Typography } from '@mui/material';
import { themePalette } from '@theme/muiTheme';
import { CloseIcon, InfoCircleEmptyIcon } from '@utils/iconsDefs';

type Props = {
  onDismiss: () => void;
};

const kbdSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  px: '6px',
  py: '2px',
  minHeight: 22,
  borderRadius: '12px',
  bgcolor: themePalette.green[600],
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '24px',
  color: themePalette.grey[1000],
};

/**
 * Pill hint for saving entity edits via keyboard — usually overlaid on the canvas.
 */
const ShortcutHint: FC<Props> = ({ onDismiss }) => {
  const isMac = useMemo(
    () =>
      typeof navigator !== 'undefined' &&
      /Mac|iPhone|iPad|iPod/.test(navigator.platform),
    [],
  );

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={2}
      sx={{
        flexWrap: 'nowrap',
        py: 2,
        px: 3,
        borderRadius: 999,
        border: `1px solid ${themePalette.green[600]}`,
        bgcolor: themePalette.green[550],
      }}
    >
      <Stack
        sx={{
          flexShrink: 0,
          display: 'flex',
          '& svg': { width: 32, height: 32, display: 'block' },
        }}
      >
        <InfoCircleEmptyIcon fill={themePalette.primary.main} />
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        sx={{ flex: 1, minWidth: 0 }}
      >
        <Typography variant="body1" color="grey.1000" component="span">
          Save your progress anytime using
        </Typography>
        <Typography variant="h5" component="span" sx={kbdSx}>
          {isMac ? '⌘S' : 'Ctrl+S'}
        </Typography>
      </Stack>

      <IconButton
        aria-label="Dismiss hint"
        onClick={onDismiss}
        size="small"
        sx={{ flexShrink: 0, p: '4px', ml: 'auto' }}
      >
        <CloseIcon width={14} height={14} fill={themePalette.grey[900]} />
      </IconButton>
    </Stack>
  );
};

export default ShortcutHint;
